import { useQuery } from '@tanstack/react-query';

import React, { useEffect, useMemo, useState } from 'react';

import { Check, Close, Edit, Hexagon, PlayArrow, Security, Visibility, VisibilityOff, VpnKey } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Divider, IconButton, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { CopyToClipboardButton } from '@@components';

import {
	ApiKeyAccessChannel,
	ApiKeyClient,
	ApiKeyDependencyModel,
	ApiKeyModel,
	ComponentModel,
	ContextClient,
	ContextScopeModel,
	EnvironmentApiKeyModel,
	EnvironmentModel,
	OperationsClient,
	SetApiKeyDependenciesRequest,
	UpdateApiKeyRequest,
} from '../../../api/api';
import { sortByName } from '../../../common/sortByName';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import { EnvironmentApiKeyAttributeKeys, environmentApiKeyAttributeRows, knownEnvironmentApiKeyAttributeKeys } from '../ApiKeyAttributes';
import AccessCredentialHelp from './AccessCredentialHelp';
import AddApiKeyDependencyDialog from './AddApiKeyDependencyDialog';
import { API_KEYS_PANEL_COLLAPSED_WIDTH, API_KEYS_PANEL_DEFAULT_WIDTH } from './ApiKeysPanel';
import AttributesList from './AttributesList';
import DependencyTree from './DependencyTree';
import EnvironmentTabs from './EnvironmentTabs';
import SectionWithAction from './SectionWithAction';

const API_KEY_DETAILS_PANEL_STORAGE_KEY = 'apiKeyDetailsPanel.width';
const API_KEY_DETAILS_PANEL_DEFAULT_WIDTH = 500;
const API_KEY_DETAILS_PANEL_MIN_WIDTH = 360;
const API_KEY_DETAILS_PANEL_MAX_WIDTH = 1000;
const API_KEY_HEADER_NAME = 'X-API-Key';

interface ApiKeyDetailsPanelProps {
	apiKeyId: string;
	environment?: EnvironmentModel | null;
	environments?: EnvironmentModel[];
	onRotateEnvironmentSecret?: (environment: EnvironmentModel, definition: ApiKeyModel) => void;
	environmentApiKey?: EnvironmentApiKeyModel | null;
	components?: ComponentModel[];
	onClose: () => void;
	onRefresh?: () => void;
	onRotateSecret?: () => void;
	listPanelCollapsed?: boolean;
	rightOffset?: number;
	top?: number;
}

export default function ApiKeyDetailsPanel({
	apiKeyId,
	environment: initialEnvironment,
	environments = [],
	onRotateEnvironmentSecret,
	environmentApiKey: initialDeployment,
	components = [],
	onClose,
	onRefresh,
	onRotateSecret,
	listPanelCollapsed = false,
	rightOffset = API_KEYS_PANEL_DEFAULT_WIDTH,
	top = 0,
}: ApiKeyDetailsPanelProps) {
	const firstEnvironmentId = environments[0]?.id;
	const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(initialEnvironment?.id ?? environments[0]?.id);
	const availableEnvironments = environments.length ? environments : initialEnvironment ? [initialEnvironment] : [];
	const environment = availableEnvironments.find((item) => item.id === selectedEnvironmentId) ?? availableEnvironments[0];
	const environmentApiKey =
		environment?.apiKeys?.find((item) => item.apiKeyId === apiKeyId) ??
		(!environments.length && environment?.id === initialEnvironment?.id ? initialDeployment : undefined);
	useEffect(() => {
		setSelectedEnvironmentId(initialEnvironment?.id ?? firstEnvironmentId);
	}, [initialEnvironment?.id, firstEnvironmentId]);
	useEffect(() => {
		setIsApiKeyValueVisible(false);
	}, [environment?.id]);
	const client = useApiClient(ApiKeyClient) as ApiKeyClient;
	const contextClient = useApiClient(ContextClient) as ContextClient;
	const operationsClient = useApiClient(OperationsClient) as OperationsClient;
	const toastService = useToastService();
	const [openAddDependencyDialog, setOpenAddDependencyDialog] = useState(false);
	const [deletingDependencyId, setDeletingDependencyId] = useState<string | null>(null);
	const [isEditingName, setIsEditingName] = useState(false);
	const [nameDraft, setNameDraft] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [isApiKeyValueVisible, setIsApiKeyValueVisible] = useState(false);
	const [panelWidth, setPanelWidth] = useState(API_KEY_DETAILS_PANEL_DEFAULT_WIDTH);

	const {
		data: apiKeyDetails,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useQuery({
		queryKey: ['apiKey', apiKeyId],
		queryFn: () => client.getById(apiKeyId),
		enabled: !!apiKeyId,
	});

	const dependencies = useMemo(() => apiKeyDetails?.dependencies || [], [apiKeyDetails?.dependencies]);
	const deploymentAttributes = useMemo(() => environmentApiKey?.attributes || {}, [environmentApiKey?.attributes]);
	const apiKeyValue = deploymentAttributes[EnvironmentApiKeyAttributeKeys.value]?.toString() ?? '';
	const hasDeploymentAttributes = Object.keys(deploymentAttributes).length > 0;
	const additionalDeploymentAttributes = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(deploymentAttributes)
					.filter(([key, value]) => !knownEnvironmentApiKeyAttributeKeys.has(key) && value !== undefined && value !== null && value !== '')
					.map(([key, value]) => [key, String(value)])
					.sort(([left], [right]) => left.localeCompare(right)),
			),
		[deploymentAttributes],
	);
	const knownDeploymentAttributes = useMemo(
		() =>
			Object.fromEntries(
				environmentApiKeyAttributeRows
					.map((row) => [row.label, deploymentAttributes[row.key]] as const)
					.filter(([, value]) => value !== undefined && value !== null && value !== '')
					.map(([label, value]) => [label, String(value)]),
			),
		[deploymentAttributes],
	);

	const { data: dependencyMetadata } = useQuery({
		queryKey: [
			'api-key-dependency-metadata',
			dependencies.map((dependency) => `${dependency.dependsOnComponentId}:${dependency.targetType}:${dependency.targetId}`).join('|'),
		],
		queryFn: async () => {
			const componentIds = Array.from(new Set(dependencies.map((dependency) => dependency.dependsOnComponentId)));
			const metadata: ApiKeyDependencyMetadata = {
				contextScopesByComponentId: {},
				operationNameById: {},
			};

			await Promise.all(
				componentIds.map(async (dependencyComponentId) => {
					const component = components.find((item) => item.id === dependencyComponentId);
					if (!component?.contextId) return;

					try {
						const [context, operations] = await Promise.all([
							contextClient.getContextById(component.contextId),
							operationsClient.getOperationsForContext(component.contextId),
						]);

						metadata.contextScopesByComponentId[dependencyComponentId] = context.scopes ?? [];
						operations.forEach((operation) => {
							metadata.operationNameById[operation.id] = operation.name;
						});
					} catch (error) {
						console.error(`Failed to load API key dependency metadata for component ${dependencyComponentId}:`, error);
					}
				}),
			);

			return metadata;
		},
		enabled: dependencies.length > 0 && components.length > 0,
	});

	const groupedDependencies = useMemo(
		() =>
			groupDependenciesByComponent(
				dependencies,
				components,
				dependencyMetadata ?? {
					contextScopesByComponentId: {},
					operationNameById: {},
				},
			),
		[components, dependencies, dependencyMetadata],
	);

	useEffect(() => {
		if (!isEditingName) {
			setNameDraft(apiKeyDetails?.name ?? '');
		}
	}, [apiKeyDetails?.name, isEditingName]);

	const handleRefresh = async () => {
		await refetch();
		onRefresh?.();
	};

	const handleCancelEditingName = () => {
		setNameDraft(apiKeyDetails?.name ?? '');
		setIsEditingName(false);
	};

	const handleSaveApiKey = async (request: UpdateApiKeyRequest, successMessage: string) => {
		if (!apiKeyDetails) return;

		setIsSaving(true);
		try {
			await client.update(apiKeyId, request);
			toastService.success(successMessage);
			setIsEditingName(false);
			await handleRefresh();
		} catch (error) {
			console.error('Error updating API key:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to update API key: ${errorMessage}`);
		} finally {
			setIsSaving(false);
		}
	};

	const handleSaveName = async () => {
		const normalizedName = nameDraft.trim();
		if (!normalizedName) {
			toastService.error('API key name is required.');
			return;
		}

		await handleSaveApiKey(
			{ name: normalizedName, description: apiKeyDetails?.description, isEnabled: apiKeyDetails?.isEnabled },
			'API key name updated successfully.',
		);
	};

	const handleToggleEnabled = async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
		await handleSaveApiKey(
			{ name: apiKeyDetails?.name, description: apiKeyDetails?.description, isEnabled: checked },
			checked ? 'API key enabled.' : 'API key disabled.',
		);
	};

	const renderApiKeyUsageValue = () => {
		if (!apiKeyValue) {
			return (
				<Typography variant="body2" color="textSecondary">
					Deploy this API key to the environment to generate a value.
				</Typography>
			);
		}

		return (
			<Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
				<Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', flexGrow: 1, minWidth: 0 }}>
					{isApiKeyValueVisible ? apiKeyValue : '••••••••••••'}
				</Typography>
				<Tooltip title={isApiKeyValueVisible ? 'Hide value' : 'Show value'}>
					<IconButton size="small" onClick={() => setIsApiKeyValueVisible((current) => !current)} sx={{ p: 0.25, flexShrink: 0 }}>
						{isApiKeyValueVisible ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
					</IconButton>
				</Tooltip>
				<CopyToClipboardButton value={apiKeyValue} size="small" iconSize={14} title="Copy API key value" edge={false} sx={{ p: 0.25, flexShrink: 0 }} />
			</Stack>
		);
	};

	const handleDeleteDependency = async (dependencyId: string, targetId: string) => {
		setDeletingDependencyId(dependencyId);
		try {
			const request: SetApiKeyDependenciesRequest = {
				remove: [targetId],
			};
			await client.setDependencies(apiKeyId, request);
			toastService.success('API key dependency removed successfully.');
			await handleRefresh();
		} catch (error) {
			console.error('Error removing API key dependency:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove API key dependency: ${errorMessage}`);
		} finally {
			setDeletingDependencyId(null);
		}
	};

	const subtitle = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1, flexShrink: 1 }}>
				ID: {apiKeyId}
			</Typography>
			<CopyToClipboardButton value={apiKeyId} size="small" iconSize={14} title="Copy ID to clipboard" edge={false} sx={{ p: 0.5 }} />
		</Box>
	);

	return (
		<>
			<SidePanelDetail
				title="API Key Details"
				subtitle={subtitle}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error as Error | null}
				errorMessage="Failed to load API key details"
				onClose={onClose}
				onRefresh={handleRefresh}
				position="stacked"
				top={top}
				rightOffset={rightOffset}
				width={panelWidth}
				zIndex={1201}
				listPanelCollapsed={listPanelCollapsed}
				listPanelCollapsedWidth={API_KEYS_PANEL_COLLAPSED_WIDTH}
				resizeStorageKey={API_KEY_DETAILS_PANEL_STORAGE_KEY}
				defaultWidth={API_KEY_DETAILS_PANEL_DEFAULT_WIDTH}
				minWidth={API_KEY_DETAILS_PANEL_MIN_WIDTH}
				maxWidth={API_KEY_DETAILS_PANEL_MAX_WIDTH}
				setWidth={setPanelWidth}
			>
				{apiKeyDetails && (
					<>
						<AccessCredentialHelp kind="apiKey" />
						<Box sx={{ mb: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Name:
								</Typography>
								{isEditingName ? (
									<>
										<TextField value={nameDraft} onChange={(event) => setNameDraft(event.target.value)} size="small" fullWidth disabled={isSaving} autoFocus />
										<Tooltip title="Save API key name">
											<span>
												<IconButton size="small" color="primary" onClick={handleSaveName} disabled={isSaving || !nameDraft.trim()} sx={{ p: 0.5 }}>
													<Check sx={{ fontSize: 16 }} />
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title="Cancel">
											<span>
												<IconButton size="small" onClick={handleCancelEditingName} disabled={isSaving} sx={{ p: 0.5 }}>
													<Close sx={{ fontSize: 16 }} />
												</IconButton>
											</span>
										</Tooltip>
									</>
								) : (
									<>
										<Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
											{apiKeyDetails.name}
										</Typography>
										<CopyToClipboardButton value={apiKeyDetails.name} size="small" iconSize={14} title="Copy name to clipboard" edge={false} />
										<Tooltip title="Edit API key name">
											<IconButton size="small" onClick={() => setIsEditingName(true)} sx={{ p: 0.5 }}>
												<Edit sx={{ fontSize: 16 }} />
											</IconButton>
										</Tooltip>
									</>
								)}
							</Stack>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Code:
								</Typography>
								<Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, flexGrow: 1 }}>
									{apiKeyDetails.code}
								</Typography>
								<CopyToClipboardButton value={apiKeyDetails.code} size="small" iconSize={14} title="Copy code to clipboard" edge={false} />
							</Stack>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Enabled
								</Typography>
								<Switch checked={apiKeyDetails.isEnabled !== false} onChange={handleToggleEnabled} disabled={isSaving} size="small" />
							</Stack>
							{apiKeyDetails.description && (
								<Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
									{apiKeyDetails.description}
								</Typography>
							)}
						</Box>

						<SectionWithAction
							title="Component Access"
							count={dependencies.length}
							onAdd={() => setOpenAddDependencyDialog(true)}
							addButtonTitle="Add component access"
							emptyMessage="No component access configured"
						>
							<DependencyTree
								root={{
									id: apiKeyDetails.id,
									label: apiKeyDetails.name,
									icon: <VpnKey sx={{ fontSize: 16, color: 'primary.main' }} />,
									children: groupedDependencies.map((group) => ({
										id: group.componentId,
										label: group.componentName,
										icon: <Hexagon sx={{ fontSize: 14, color: 'primary.main' }} />,
										children: group.channels.map((channel) => ({
											id: `${group.componentId}-${channel.accessChannel}`,
											label: channel.label,
											icon: <Security sx={{ fontSize: 14, color: 'primary.main' }} />,
											children: [
												...channel.scopes.map((dependency) => ({
													id: dependency.id,
													label: renderDependencyLabel(dependency.name, dependency.isMissing),
													icon: <Security sx={{ fontSize: 14, color: dependency.isMissing ? 'warning.main' : 'primary.main' }} />,
													action: createRemoveDependencyAction(dependency.id, dependency.targetId),
												})),
												...channel.operations.map((dependency) => ({
													id: dependency.id,
													label: renderDependencyLabel(dependency.name, dependency.isMissing),
													icon: <PlayArrow sx={{ fontSize: 14, color: dependency.isMissing ? 'warning.main' : 'primary.main' }} />,
													action: createRemoveDependencyAction(dependency.id, dependency.targetId),
												})),
											],
										})),
									})),
								}}
							/>
						</SectionWithAction>

						<Divider sx={{ my: 2 }} />

						<EnvironmentTabs
							environments={availableEnvironments}
							environmentId={environment?.id}
							disabled={isSaving}
							label="API Key environments"
							onChange={setSelectedEnvironmentId}
						/>
						{!environmentApiKey && (
							<Alert severity="info">
								{environment ? 'Deploy this API key from the environment node to see its credentials.' : 'No environments available.'}
							</Alert>
						)}

						{environmentApiKey && (
							<>
								<Stack spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
										Actions
									</Typography>
									{(onRotateEnvironmentSecret || onRotateSecret) && (
										<Button
											variant="outlined"
											size="small"
											startIcon={<VpnKey />}
											onClick={() => {
												if (environment && onRotateEnvironmentSecret) {
													onRotateEnvironmentSecret(environment, apiKeyDetails);
												} else {
													onRotateSecret?.();
												}
											}}
										>
											Rotate Key
										</Button>
									)}
								</Stack>

								<Box
									sx={{
										mb: 2,
										px: 1.5,
										py: 1.25,
										border: 1,
										borderColor: 'divider',
										borderRadius: 1,
										backgroundColor: 'action.hover',
									}}
								>
									<Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
										API Key Usage
									</Typography>
									<Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
										Send the API key in this request header.
									</Typography>
									<Stack spacing={1}>
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="body2" color="textSecondary" sx={{ width: 72, flexShrink: 0 }}>
												Header
											</Typography>
											<Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1, minWidth: 0 }}>
												{API_KEY_HEADER_NAME}
											</Typography>
											<CopyToClipboardButton
												value={API_KEY_HEADER_NAME}
												size="small"
												iconSize={14}
												title="Copy API key header name"
												edge={false}
												sx={{ p: 0.25 }}
											/>
										</Stack>
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="body2" color="textSecondary" sx={{ width: 72, flexShrink: 0 }}>
												Value
											</Typography>
											{renderApiKeyUsageValue()}
										</Stack>
									</Stack>
								</Box>

								<AttributesList
									attributes={{ ...knownDeploymentAttributes, ...additionalDeploymentAttributes }}
									title="Deployment Attributes"
									emptyMessage="The key value will appear after the API key deployment release plan completes."
									secretKeys={['API Key', 'ApiKey:Value']}
								/>

								{!hasDeploymentAttributes && <Divider sx={{ my: 2 }} />}
							</>
						)}
					</>
				)}
			</SidePanelDetail>

			<AddApiKeyDependencyDialog
				open={openAddDependencyDialog}
				apiKeyId={apiKeyId}
				components={components}
				existingDependencies={dependencies}
				onClose={() => setOpenAddDependencyDialog(false)}
				onDependencyAdded={() => {
					setOpenAddDependencyDialog(false);
					handleRefresh();
				}}
			/>
		</>
	);

	function createRemoveDependencyAction(dependencyId: string, targetId: string) {
		return (
			<Tooltip title="Remove dependency">
				<span>
					<IconButton
						size="small"
						onClick={() => handleDeleteDependency(dependencyId, targetId)}
						disabled={deletingDependencyId === dependencyId}
						color="error"
					>
						<Close fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		);
	}
}

interface ApiKeyDependencyMetadata {
	contextScopesByComponentId: Record<string, ContextScopeModel[]>;
	operationNameById: Record<string, string>;
}

interface GroupedDependencyItem {
	id: string;
	targetId: string;
	name: string;
	isMissing?: boolean;
}

interface GroupedDependencyChannel {
	accessChannel: ApiKeyAccessChannel;
	label: string;
	scopes: GroupedDependencyItem[];
	operations: GroupedDependencyItem[];
}

interface GroupedDependency {
	componentId: string;
	name: string;
	componentName: string;
	channels: GroupedDependencyChannel[];
}

function groupDependenciesByComponent(
	dependencies: ApiKeyDependencyModel[],
	components: ComponentModel[],
	metadata: ApiKeyDependencyMetadata,
): GroupedDependency[] {
	const grouped = new Map<string, GroupedDependency>();

	dependencies.forEach((dependency) => {
		const component = components.find((item) => item.id === dependency.dependsOnComponentId);
		const group =
			grouped.get(dependency.dependsOnComponentId) ??
			({
				componentId: dependency.dependsOnComponentId,
				name: component?.name || dependency.dependsOnComponentId,
				componentName: component?.name || dependency.dependsOnComponentId,
				channels: [],
			} satisfies GroupedDependency);
		let channel = group.channels.find((item) => item.accessChannel === dependency.accessChannel);
		if (!channel) {
			channel = {
				accessChannel: dependency.accessChannel,
				label: getAccessChannelLabel(dependency.accessChannel),
				scopes: [],
				operations: [],
			};
			group.channels.push(channel);
		}

		if (dependency.targetType === 'Scope') {
			const scope = metadata.contextScopesByComponentId[dependency.dependsOnComponentId]?.find((item) => item.id === dependency.targetId);
			channel.scopes.push({
				id: dependency.id || dependency.targetId,
				targetId: dependency.targetId,
				name: scope?.name || dependency.targetId,
				isMissing: !scope,
			});
		} else if (dependency.targetType === 'Operation') {
			channel.operations.push({
				id: dependency.id || dependency.targetId,
				targetId: dependency.targetId,
				name: metadata.operationNameById[dependency.targetId] || dependency.targetId,
				isMissing: !metadata.operationNameById[dependency.targetId],
			});
		}

		grouped.set(dependency.dependsOnComponentId, group);
	});

	return Array.from(grouped.values())
		.map((group) => ({
			...group,
			channels: group.channels
				.map((channel) => ({
					...channel,
					scopes: channel.scopes.sort(sortByName),
					operations: channel.operations.sort(sortByName),
				}))
				.sort((left, right) => getAccessChannelSortOrder(left.accessChannel) - getAccessChannelSortOrder(right.accessChannel)),
		}))
		.sort(sortByName);
}

function getAccessChannelLabel(accessChannel: ApiKeyAccessChannel) {
	if (accessChannel === ApiKeyAccessChannel.Api) return 'API';
	if (accessChannel === ApiKeyAccessChannel.Mcp) return 'MCP';
	return 'API + MCP';
}

function getAccessChannelSortOrder(accessChannel: ApiKeyAccessChannel) {
	if (accessChannel === ApiKeyAccessChannel.ApiAndMcp) return 0;
	if (accessChannel === ApiKeyAccessChannel.Api) return 1;
	return 2;
}

function renderDependencyLabel(name: string, isMissing?: boolean) {
	return (
		<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
			<Typography variant="body2" noWrap color={isMissing ? 'warning.main' : undefined}>
				{name}
			</Typography>
			{isMissing && <Chip label="Missing" color="warning" size="small" variant="outlined" sx={{ height: 18 }} />}
		</Stack>
	);
}
